import React, { useId, useState } from 'react';
import {
    ContentPage,
    ContentSection,
    PAGE_LIMITS,
    SECTION_ALIGNS,
    SECTION_BACKGROUNDS,
    SECTION_LAYOUTS,
    SECTION_WIDTHS,
    ToolContentKind,
    BlockAccent,
    BlockAppearance,
    BlockSpace,
    cleanAnchor,
} from '@easytools/tool-content';
import { ImageField } from './MediaLibrary';

/** Impostazioni della pagina: dati per i motori di ricerca (pagina pubblica) e il prompt per il Desktop Agent. */
export const PageSettings: React.FC<{
    kind: ToolContentKind;
    page: ContentPage;
    sections: number;
    blocks: number;
    onChange: (page: ContentPage, field: keyof ContentPage) => void;
    onOpenPrompt: () => void;
}> = ({ kind, page, sections, blocks, onChange, onOpenPrompt }) => {
    const ids = { title: useId(), description: useId() };
    const set = (key: keyof ContentPage, value: string) => onChange({ ...page, [key]: value }, key);
    const prompt = page.agentNotes?.trim() ?? '';
    const count = (key: 'title' | 'description') => {
        const length = (page[key] ?? '').length;
        return <span className={`tcs-count${length > PAGE_LIMITS[key] ? ' is-over' : ''}`}>{length} / {PAGE_LIMITS[key]}</span>;
    };

    return (
        <div className="tcs-fields">
            <p className="tcs-fields__note">
                {sections === 0 ? 'La pagina è vuota.' : `${sections} ${sections === 1 ? 'sezione' : 'sezioni'}, ${blocks} ${blocks === 1 ? 'blocco' : 'blocchi'}.`}
                {' '}Clicca un testo nell'anteprima per scriverlo, un blocco o una sezione per il resto.
            </p>

            {kind === 'publicPage' && (
                <fieldset className="tcs-group">
                    <legend>Motori di ricerca e condivisione</legend>
                    <div className="tcs-fields__field">
                        <div className="tcs-fields__labelrow"><label className="tcs-fields__label" htmlFor={ids.title}>Titolo</label>{count('title')}</div>
                        <input id={ids.title} className="form-control form-control-sm" value={page.title ?? ''}
                            placeholder="Gestione listini per Dynamics 365 | Semplora" onChange={e => set('title', e.target.value)} />
                    </div>
                    <div className="tcs-fields__field">
                        <div className="tcs-fields__labelrow"><label className="tcs-fields__label" htmlFor={ids.description}>Descrizione</label>{count('description')}</div>
                        <textarea id={ids.description} className="form-control form-control-sm" rows={3} value={page.description ?? ''}
                            onChange={e => set('description', e.target.value)} />
                        <div className="tcs-fields__hint">Compare sotto il titolo nei risultati di Google e nelle anteprime dei link.</div>
                    </div>
                    <ImageField label="Immagine per le anteprime dei link" value={page.image ?? ''} onChange={url => set('image', url)} />
                </fieldset>
            )}

            <fieldset className="tcs-group">
                <legend>Prompt per il Desktop Agent</legend>
                {prompt
                    ? <p className="tcs-group__quote">{prompt.length > 220 ? `${prompt.slice(0, 220)}…` : prompt}</p>
                    : <p className="tcs-fields__note">Nessun prompt salvato per questa pagina.</p>}
                <button type="button" className="btn btn-sm btn-light" onClick={onOpenPrompt}>
                    {prompt ? 'Modifica e rigenera' : 'Scrivi un prompt'}
                </button>
                <div className="tcs-fields__hint">Si salva con la bozza e non compare mai nella pagina.</div>
            </fieldset>
        </div>
    );
};

function Choice<T extends string>({ legend, value, options, onChange }: {
    legend: string;
    value: T;
    options: { value: T; label: string }[];
    onChange: (value: T) => void;
}) {
    return (
        <fieldset className="tcs-choice">
            <legend className="tcs-fields__label">{legend}</legend>
            <div className="tcs-choice__options" role="radiogroup">
                {options.map(o => (
                    <button key={o.value} type="button" role="radio" aria-checked={value === o.value}
                        className={`tcs-choice__option${value === o.value ? ' is-on' : ''}`} onClick={() => onChange(o.value)}>
                        {o.label}
                    </button>
                ))}
            </div>
        </fieldset>
    );
}

/** Stile di una sezione: sfondo, larghezza, colonne e allineamento. */
export const SectionSettings: React.FC<{
    section: ContentSection;
    onChange: (patch: Partial<ContentSection>) => void;
}> = ({ section, onChange }) => (
    <div className="tcs-fields">
        <div className="tcs-swatches" role="radiogroup" aria-label="Sfondo">
            <span className="tcs-fields__label">Sfondo</span>
            <div className="tcs-swatches__row">
                {SECTION_BACKGROUNDS.map(o => (
                    <button key={o.value} type="button" role="radio" aria-checked={(section.background ?? 'plain') === o.value}
                        className={`tcs-swatches__item${(section.background ?? 'plain') === o.value ? ' is-on' : ''}`}
                        onClick={() => onChange({ background: o.value })}>
                        <span className={`tcs-outline__swatch tcs-outline__swatch--${o.value}`} aria-hidden="true" />{o.label}
                    </button>
                ))}
            </div>
        </div>
        <Choice legend="Colonne" value={section.layout ?? 'stack'} options={SECTION_LAYOUTS} onChange={layout => onChange({ layout })} />
        {section.layout === 'split' && (
            <p className="tcs-fields__hint mt-0">I blocchi si affiancano a coppie, nell'ordine: il primo a sinistra, il secondo a destra, e così via. Su telefono tornano uno sotto l'altro.</p>
        )}
        <Choice legend="Larghezza" value={section.width ?? 'normal'} options={SECTION_WIDTHS} onChange={width => onChange({ width })} />
        <Choice legend="Allineamento del testo" value={section.align ?? 'start'} options={SECTION_ALIGNS} onChange={align => onChange({ align })} />
    </div>
);

const SPACES = [
    { value: 'tight', label: 'Ridotto' },
    { value: '', label: 'Normale' },
    { value: 'wide', label: 'Ampio' },
    { value: 'xwide', label: 'Molto' },
];
const ALIGNS = [
    { value: '', label: 'Come la sezione' },
    { value: 'start', label: 'Sinistra' },
    { value: 'center', label: 'Centro' },
    { value: 'end', label: 'Destra' },
];
const VISIBILITY = [
    { value: '', label: 'Sempre' },
    { value: 'desktop', label: 'Solo telefono' },
    { value: 'phone', label: 'Solo computer' },
];
const ACCENTS: { value: BlockAccent | ''; label: string; color: string }[] = [
    { value: '', label: 'Predefinito', color: '#3b82f6' },
    { value: 'teal', label: 'Petrolio', color: '#0f8a8a' },
    { value: 'green', label: 'Verde', color: '#16a34a' },
    { value: 'violet', label: 'Viola', color: '#7c3aed' },
    { value: 'amber', label: 'Ambra', color: '#c2680c' },
    { value: 'rose', label: 'Rosa', color: '#e11d48' },
    { value: 'navy', label: 'Blu notte', color: '#0d1b2a' },
];

/**
 * Aspetto del singolo blocco: spazio, allineamento, colore d'accento, visibilità e ancora.
 * Un valore vuoto toglie il campo, così il blocco segue la sezione.
 */
export const AppearanceFields: React.FC<{
    appearance?: BlockAppearance;
    onChange: (appearance: BlockAppearance | undefined) => void;
}> = ({ appearance = {}, onChange }) => {
    const anchorId = useId();
    const [copied, setCopied] = useState(false);
    const set = <K extends keyof BlockAppearance>(key: K, value: BlockAppearance[K] | '') => {
        const next: BlockAppearance = { ...appearance };
        if (value) next[key] = value as BlockAppearance[K]; else delete next[key];
        onChange(Object.keys(next).length ? next : undefined);
    };
    const anchor = cleanAnchor(appearance.anchor);

    return (
        <div className="tcs-fields">
            <Choice legend="Spazio sopra" value={appearance.spaceTop ?? ''} options={SPACES} onChange={v => set('spaceTop', v as BlockSpace | '')} />
            <Choice legend="Spazio sotto" value={appearance.spaceBottom ?? ''} options={SPACES} onChange={v => set('spaceBottom', v as BlockSpace | '')} />
            <Choice legend="Allineamento" value={appearance.align ?? ''} options={ALIGNS} onChange={v => set('align', v as BlockAppearance['align'] | '')} />
            <fieldset className="tcs-choice">
                <legend className="tcs-fields__label">Colore d’accento</legend>
                <div className="tcs-accents" role="radiogroup" aria-label="Colore d’accento">
                    {ACCENTS.map(a => (
                        <button key={a.value || 'default'} type="button" role="radio" aria-checked={(appearance.accent ?? '') === a.value}
                            aria-label={a.label} title={a.label}
                            className={`tcs-accents__item${(appearance.accent ?? '') === a.value ? ' is-on' : ''}`}
                            style={{ '--swatch': a.color } as React.CSSProperties}
                            onClick={() => set('accent', a.value)} />
                    ))}
                </div>
                <div className="tcs-fields__hint">Icone, numeri dei passi, link e dettagli del blocco.</div>
            </fieldset>
            <Choice legend="Visibilità" value={appearance.hideOn ?? ''} options={VISIBILITY} onChange={v => set('hideOn', v as BlockAppearance['hideOn'] | '')} />
            <div className="tcs-fields__field">
                <label className="tcs-fields__label" htmlFor={anchorId}>Ancora per i link interni</label>
                <div className="input-group input-group-sm">
                    <span className="input-group-text">#</span>
                    <input id={anchorId} className="form-control" value={appearance.anchor ?? ''} placeholder="prezzi"
                        onChange={e => set('anchor', e.target.value)} onBlur={e => set('anchor', cleanAnchor(e.target.value))} />
                    {anchor && (
                        <button type="button" className="btn btn-outline-secondary" onClick={async () => {
                            try { await navigator.clipboard.writeText(`#${anchor}`); setCopied(true); window.setTimeout(() => setCopied(false), 1500); } catch { /* il testo resta visibile nel campo */ }
                        }}>{copied ? 'Copiato' : 'Copia'}</button>
                    )}
                </div>
                <div className="tcs-fields__hint">
                    {anchor ? `Un pulsante o un link con indirizzo #${anchor} porta a questo blocco.` : 'Facoltativa: lettere minuscole, cifre e trattini.'}
                </div>
            </div>
            {Object.keys(appearance).length > 0 && (
                <button type="button" className="btn btn-sm btn-light align-self-start" onClick={() => onChange(undefined)}>Torna all’aspetto predefinito</button>
            )}
        </div>
    );
};
